import React from 'react';

import { BannerScreen } from '../screens/BannerScreen';
import { EmailDomainScreen } from '../screens/EmailDomainScreen';
import { MemberTypeApprovalScreen } from '../screens/MemberTypeApprovalScreen';
import { OrderIdClaimScreen } from '../screens/OrderIdClaimScreen';
import { QuotaScreen } from '../screens/QuotaScreen';
import { SlaMonitorScreen } from '../screens/SlaMonitorScreen';
import { VerificationExpiryScreen } from '../screens/VerificationExpiryScreen';

import { useCRMHost } from './CRMHostContext';
import type { CrmScreenKey } from './CrmApiPort';
import { BulkReviewContainer } from './containers/BulkReviewContainer';
import { ClaimQueueContainer } from './containers/ClaimQueueContainer';
import { CouponContainer } from './containers/CouponContainer';
import { PointRuleContainer } from './containers/PointRuleContainer';
import { PrivilegeContainer } from './containers/PrivilegeContainer';
import { RewardCatalogContainer } from './containers/RewardCatalogContainer';
import { TierContainer } from './containers/TierContainer';

/**
 * แปลงคีย์ของหน้าจอเป็น component
 *
 * ทำไมเป็น prop ไม่ใช่ router — โมดูลนี้ต้องรันได้ทั้งใน react-router (standalone)
 * และในเมนู admin ของ host ที่ไม่มี URL ของตัวเอง
 * การผูกกับ router ตัวใดตัวหนึ่งจะทำให้อีกโหมดใช้ไม่ได้
 *
 * หน้าจอที่ยังไม่ได้ต่อ backend จะแสดงข้อมูลจำลองของตัวเอง เท่ากับของเดิมทุกประการ
 * (ดู เอกสารวิเคราะห์ช่องว่างของ API ที่ทีมเก็บไว้ภายใน — backend ครอบคลุมราว 25% ของที่หลังบ้านต้องใช้)
 */
export const CrmScreenRouter: React.FC<{ screen: CrmScreenKey }> = ({ screen }) => {
  const { linkComponent } = useCRMHost();

  switch (screen) {
    /* ── กลุ่มที่ 1 — คิวตรวจสอบคำขอสะสมคะแนน (FR-ADM-10 ถึง 14) ── */
    case 'claim-queue':
      return <ClaimQueueContainer />;
    case 'claim-order-id':
      return <OrderIdClaimScreen />;
    case 'claim-bulk':
      return <BulkReviewContainer />;
    case 'sla-monitor':
      return <SlaMonitorScreen linkComponent={linkComponent} />;

    /* ── กลุ่มที่ 2 — การจัดการประเภทสมาชิก (ยังไม่มี API) ── */
    case 'member-type-approval':
      return <MemberTypeApprovalScreen linkComponent={linkComponent} />;
    case 'email-domain':
      return <EmailDomainScreen />;
    case 'verification-expiry':
      return <VerificationExpiryScreen />;
    case 'quota':
      return <QuotaScreen />;

    /* ── กลุ่มที่ 3 — การตั้งค่าโปรแกรมสะสมคะแนน ── */
    case 'point-rule':
      return <PointRuleContainer />;
    case 'tier':
      return <TierContainer />;
    case 'reward-catalog':
      return <RewardCatalogContainer />;
    case 'privilege':
      return <PrivilegeContainer />;
    case 'coupon':
      return <CouponContainer />;
    case 'banner':
      return <BannerScreen />;

    default: {
      /* บังคับให้ TypeScript ฟ้องถ้ามีคีย์ใหม่แล้วลืมเพิ่มที่นี่ */
      const exhaustive: never = screen;
      return <div>ไม่รู้จักหน้าจอ: {String(exhaustive)}</div>;
    }
  }
};
